/**
 * Main App Component
 *
 * Root component with routing, authentication, and layout.
 */

import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider } from './context/AuthContext';
import useAuth from './hooks/useAuth';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Layout
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';

// Pages
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import RoomList from './components/Rooms/RoomList';
import RoomForm from './components/Rooms/RoomForm';
import RoomDetail from './components/Rooms/RoomDetail';
import GameList from './components/Games/GameList';
import PlayerList from './components/Players/PlayerList';
import CardList from './components/Cards/CardList';

import { ROUTES } from './utils/constants';

/**
 * Protected Route Component
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner-large"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to={ROUTES.LOGIN} replace />;
};

/**
 * Main Layout Component
 */
const MainLayout = ({ children, pageTitle }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="app-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="app-content">
        <Header title={pageTitle} onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <main className="main-content">{children}</main>
        <Footer />
      </div>
    </div>
  );
};

/**
 * App Routes Component
 */
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path={ROUTES.LOGIN} element={<Login />} />

      {/* Protected Routes */}
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <ProtectedRoute>
            <MainLayout pageTitle="Dashboard">
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Rooms Routes */}
      <Route
        path={ROUTES.ROOMS}
        element={
          <ProtectedRoute>
            <MainLayout pageTitle="Salas">
              <RoomList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.ROOMS}/create`}
        element={
          <ProtectedRoute>
            <MainLayout pageTitle="Nueva Sala">
              <RoomForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.ROOMS}/:id`}
        element={
          <ProtectedRoute>
            <MainLayout pageTitle="Detalle de Sala">
              <RoomDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.ROOMS}/:id/edit`}
        element={
          <ProtectedRoute>
            <MainLayout pageTitle="Editar Sala">
              <RoomForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Games Routes */}
      <Route
        path={ROUTES.GAMES}
        element={
          <ProtectedRoute>
            <MainLayout pageTitle="Juegos">
              <GameList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Players Routes */}
      <Route
        path={ROUTES.PLAYERS}
        element={
          <ProtectedRoute>
            <MainLayout pageTitle="Jugadores">
              <PlayerList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Cards Routes */}
      <Route
        path={ROUTES.CARDS}
        element={
          <ProtectedRoute>
            <MainLayout pageTitle="Cartones">
              <CardList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Redirect to dashboard */}
      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  );
};

/**
 * Main App Component
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
